import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import dict from "../i18n/es-MX";

/**
 * Banner que aparece cuando el Service Worker detecta una versión nueva.
 * Cuando el usuario hace click en "Actualizar ahora", manda SKIP_WAITING al
 * SW en espera y recarga la página.
 */
export default function SWUpdateBanner() {
    const [waiting, setWaiting] = useState(null);

    useEffect(() => {
        if (!("serviceWorker" in navigator)) return undefined;

        let intervalId = null;
        let registration = null;

        const onUpdateFound = () => {
            const installing = registration?.installing;
            if (!installing) return;
            installing.addEventListener("statechange", () => {
                if (installing.state === "installed" && navigator.serviceWorker.controller) {
                    setWaiting(installing);
                }
            });
        };

        const onControllerChange = () => {
            // The new SW activated. Force a reload to pick up the fresh bundle.
            window.location.reload();
        };

        navigator.serviceWorker.register("/service-worker.js").then((reg) => {
            registration = reg;
            // If there is already a waiting SW when the tab opens, show banner.
            if (reg.waiting && navigator.serviceWorker.controller) {
                setWaiting(reg.waiting);
            }
            reg.addEventListener("updatefound", onUpdateFound);
            // Poll for updates every 5 min.
            intervalId = setInterval(() => reg.update().catch(() => {}), 5 * 60 * 1000);
        }).catch(() => {});

        navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

        return () => {
            if (intervalId) clearInterval(intervalId);
            navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
        };
    }, []);

    const apply = () => {
        if (!waiting) return;
        waiting.postMessage({ type: "SKIP_WAITING" });
        // controllerchange listener will handle the reload.
    };

    if (!waiting) return null;

    return (
        <div
            data-testid="sw-update-banner"
            className="fixed bottom-4 right-4 z-[60] max-w-sm rounded-2xl border border-brand-orange/60 bg-slate-950/95 backdrop-blur-sm shadow-2xl shadow-brand-orange/20 p-4"
        >
            <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-brand-orange/15 inline-flex items-center justify-center shrink-0">
                    <RefreshCw size={16} className="text-brand-orange"/>
                </div>
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-100">{dict.sw.title}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{dict.sw.description}</div>
                </div>
            </div>
            <div className="flex gap-2 mt-3">
                <button
                    type="button"
                    data-testid="sw-update-apply"
                    onClick={apply}
                    className="flex-1 h-9 rounded-lg bg-brand-orange hover:bg-orange-400 text-slate-950 font-semibold uppercase text-xs tracking-wider"
                >
                    {dict.sw.applyNow}
                </button>
                <button
                    type="button"
                    data-testid="sw-update-dismiss"
                    onClick={() => setWaiting(null)}
                    className="h-9 px-3 rounded-lg border border-slate-700 hover:bg-slate-800 text-xs uppercase tracking-wider text-slate-300"
                >
                    {dict.sw.later}
                </button>
            </div>
        </div>
    );
}
